import type { MutableRefObject, FC } from 'react';
import React, { useState, useEffect, Fragment } from 'react';
import { Route, withRouter } from 'react-router-dom';
import type { TrackingProp } from 'react-tracking';
import track from 'react-tracking';
import Address from '@ynap/address';
import BEMHelper from '@ynap/bem-helper';
import { SimpleAsyncButton } from '@ynap/button';
import type { FormValues, FieldRenderProps, FormRefObject } from '@ynap/form';
import { Form, Field as FormField } from '@ynap/form';
import Notification, { Tooltip } from '@ynap/notification';
import { StopPropagation } from '@ynap/overlay';
import { RadioButtonGroup, RadioButton } from '@ynap/radio-button';
import { AddressForm } from '../AddressForm';
import { mapAddressValues, collateErrors } from '../AddressForm/utils';
import AddAddressRadioButton from './AddAddressRadioButton';
import AddAddressRoute from './AddAddressRoute';
import AddressRadioButton from './AddressRadioButton';
import EditAddressRoute from './EditAddressRoute';
import type { AddAddressAction, EditAddressAction } from './@types';
import type { Locale, Config, Messages, CheckoutAnalytics } from '../AddressForm/@types';

type AnyFn = (...args: unknown[]) =>  Partial<object>;
const bem = new BEMHelper('AddressSelectionForm');

interface AddressSelectionFormProps {
    addAddress?: AddAddressAction;
    addresses?: Array<FormValues>;
    addressFormTouched?: boolean;
    addressIdValue: number;
    addTitle: string;
    childRef?: MutableRefObject<FormRefObject>;
    className: string;
    config: Config;
    defaultAddress?: FormValues;
    defaultAddressLabel?: string;
    editAddress?: EditAddressAction;
    editTitle: string;
    error?: string;
    form: string;
    history: { push: (url: string) => void };
    initialValues?: FormValues;
    locale: Locale;
    name?: string;
    onSubmit?: AnyFn;
    otherAddressLabel?: string;
    shippingRestricted?: boolean;
    submitting?: boolean;
    match: { path: string; url: string };
    messages?: Messages;
    reduxForm?: { change: AnyFn; name: string; blur: any; syncErrors: any };
    trackAddNewAddress?: AnyFn;
    trackConfirmAddNewAddress?: () => void;
    trackEditAddress?: AnyFn;
    trackUpdateAddress?: AnyFn;
    trackOnSubmitButtonClick?: AnyFn;
    trackAddressShippingRestricted?: AnyFn;
    tracking: TrackingProp;
    isCheckoutLayout?: boolean;
    isSelectedAddressValid?: boolean;
    editable?: boolean;
    showChangeAddress?: boolean;
    onChange?: AnyFn;
    split?: boolean;
    hideCountry?: boolean;
    addressFormClass?: string;
    primaryCategory?: string;
    checkoutAnalytics?: CheckoutAnalytics;
    isGuestUser?: boolean;
    setAddressForm?: AnyFn;
    setSelectAddressForm?: AnyFn;
    shouldDisplayAddressForm?: boolean;
    onEditModeChange?: AnyFn;
    handleFormCancel?: AnyFn;
    billingAddressValidation?: { address: number; invalidFields: string[] };
}

type EditAddressState = number | string;

export const AddressSelectionForm: FC<AddressSelectionFormProps> = (props) => {
    const {
        addAddress,
        addresses = [],
        addressFormClass,
        addressFormTouched,
        addTitle,
        billingAddressValidation,
        checkoutAnalytics,
        childRef,
        className,
        config,
        defaultAddress,
        defaultAddressLabel,
        editable = true,
        editAddress,
        editTitle,
        error,
        handleFormCancel,
        hideCountry = false,
        history,
        initialValues,
        isCheckoutLayout = true,
        isGuestUser,
        isSelectedAddressValid,
        locale,
        match,
        messages = {},
        name,
        onChange,
        onSubmit,
        otherAddressLabel,
        primaryCategory,
        reduxForm,
        setAddressForm,
        setSelectAddressForm,
        shippingRestricted = false,
        shouldDisplayAddressForm,
        showChangeAddress = false,
        split = false,
        trackAddNewAddress,
        trackAddressShippingRestricted,
        trackConfirmAddNewAddress,
        trackEditAddress,
        tracking,
        trackOnSubmitButtonClick,
        trackUpdateAddress,
    } = props;

    const addressIdValue =
        initialValues && initialValues.addressId && (typeof initialValues.addressId == 'string' || typeof initialValues.addressId == 'number')
            ? initialValues.addressId.toString()
            : undefined;
    const isRegisteredAndRestricted = shippingRestricted && addresses.length && initialValues?.addressId === Number(addressIdValue);

    const [enableContinue, setEnableContinue] = useState(!isRegisteredAndRestricted);
    const [removeAddressFinder, setRemoveAddressFinder] = useState(() => () => null);

    // We had an issue (SHOP-4546) while editing an already existing address mid checkout flow where we would have unnecessary
    // renders of the address list after the address was edited and before the cart api was called with the updated address id.
    // So we had to come up with a mechanism to track this flow and prevent those renders from happening since they
    // didn't add anything to the user since there was no interaction available anyway.
    // This will also help along the line using !! to force cast to boolean
    // 0 -> start
    // 1 -> during
    // > 1 -> the id of the address that was just edited (also used to pass the address id to relevante components)
    const [saveEditing, setSaveEditing] = useState(0);

    const invalidSelectedAddress = isSelectedAddressValid === false ? addressIdValue : '';
    const [editAddressID, setEditAddressID] = useState<EditAddressState | undefined>(invalidSelectedAddress);

    // useEffect to open the address form when any field in the selected billing address is invalid.
    useEffect(() => {
        if (Object.prototype.hasOwnProperty.call(billingAddressValidation ?? {}, 'invalidFields') && billingAddressValidation?.invalidFields?.length) {
            setEditAddressID(Number(billingAddressValidation.address));
        }
    }, [billingAddressValidation]);

    // We setEditAddressID if we know the user clicked a billing address with Klarna payment
    useEffect(() => {
        if (reduxForm?.syncErrors?.billingAddress?.isKlarna) {
            setEditAddressID(Number(reduxForm?.syncErrors?.billingAddress?.addressId));
        }
    }, [reduxForm?.syncErrors]);

    useEffect(() => {
        const isDefaultAddress = addresses.some((address) => initialValues && initialValues.addressId === address.addressId);

        if (shippingRestricted && trackAddressShippingRestricted) {
            tracking.trackEvent(trackAddressShippingRestricted({ defaultAddress: isDefaultAddress }));
          }
    }, [addresses, initialValues, shippingRestricted, trackAddressShippingRestricted, tracking]);

    useEffect(() => {
        // show the address form if user choose an invalid address
        if (invalidSelectedAddress) setEditAddressID(Number(invalidSelectedAddress));
    }, [invalidSelectedAddress]);

    const handleSubmit = async (values: FormValues) => {
        const mappedValues = mapAddressValues(values, locale);
        trackConfirmAddNewAddress?.();
        onChange?.(mappedValues);
        return onSubmit?.(mappedValues);
    };
    const formClass = className ? bem(className, 'isCheckoutLayout') : bem(undefined, 'isCheckoutLayout');

    const isEditing = (Array.isArray(addresses) && !!addresses.find((it) => it.addressId === editAddressID)) || !!saveEditing;
    const isAdding = editAddressID === 'add' || (!isEditing && shouldDisplayAddressForm);

    useEffect(() => {
        if (isAdding || isEditing) {
            setAddressForm?.(true);
        }
    }, [isEditing, isAdding, setAddressForm]);

    function renderAddress() {
        if (!editable && addAddress && !addresses?.length) {
          return (
            <Fragment>
              <StopPropagation>
                <AddAddressRoute
                  addAddress={addAddress}
                  addTitle={addTitle}
                  checkoutAnalytics={checkoutAnalytics}
                  config={config}
                  formTitle={messages?.addressSelectionForm?.addAddress?.()}
                  hideCountry={hideCountry}
                  history={history}
                  isCheckoutLayout={isCheckoutLayout}
                  locale={locale}
                  match={match}
                  messages={messages}
                  onSubmit={handleSubmit}
                  primaryCategory={primaryCategory}
                  removeAddressFinder={removeAddressFinder}
                  setEditAddressID={setEditAddressID}
                  setRemoveAddressFinder={setRemoveAddressFinder}
                  trackConfirmAddNewAddress={trackConfirmAddNewAddress}
                  tracking={tracking}
                  withOverlay={false}
                />
              </StopPropagation>
            </Fragment>
          );
        }
      
        if (!addresses || !addresses.length) {
          if (defaultAddress) {
            return (
              <Fragment>
                <FormField
                  name="addressId"
                  value={String(initialValues?.addressId || defaultAddress?.addressId || addresses?.[0]?.addressId || '')}
                >
                  {({ onChange, value }: FieldRenderProps) => (
                    <Fragment>
                      <RadioButton
                        checked={typeof value == 'undefined' || value == defaultAddress.addressId}
                        className={bem('radioButton')}
                        description={<Address {...defaultAddress} />}
                        id={String(defaultAddress.addressId)}
                        label={defaultAddressLabel}
                        onChange={(ev: React.ChangeEvent<HTMLInputElement>) => onChange(ev.target.value)}
                        value={defaultAddress.addressId}
                      />
                      <RadioButton
                        checked={value != defaultAddress.addressId}
                        className={bem('radioButton')}
                        id="newBillingAddress"
                        label={otherAddressLabel}
                        value={addressIdValue !== defaultAddress.addressId ? addressIdValue : ''}
                        onChange={() => {
                          const newAddressId = addressIdValue != defaultAddress.addressId ? addressIdValue || '' : '';
                          onChange(newAddressId);
                        }}
                      />
                      {value != defaultAddress.addressId ? (
                        <AddressForm
                          checkoutAnalytics={checkoutAnalytics}
                          className={addressFormClass || bem('addressForm')}
                          config={config}
                          country={locale.country}
                          hideCountry={hideCountry}
                          initialValues={initialValues}
                          isCheckoutLayout={isCheckoutLayout}
                          messages={messages}
                          primaryCategory={primaryCategory}
                          removeAddressFinder={removeAddressFinder}
                          setRemoveAddressFinder={setRemoveAddressFinder}
                          tracking={tracking}
                        />
                      ) : null}
                    </Fragment>
                  )}
                </FormField>
              </Fragment>
            );
          }
 
            return (
              <>
                <AddressForm
                  checkoutAnalytics={checkoutAnalytics}
                  className={addressFormClass || bem('addressForm')}
                  config={config}
                  country={locale.country}
                  hideCountry={hideCountry}
                  initialValues={initialValues}
                  isCheckoutLayout={isCheckoutLayout}
                  messages={messages}
                  primaryCategory={primaryCategory}
                  removeAddressFinder={removeAddressFinder}
                  setRemoveAddressFinder={setRemoveAddressFinder}
                  tracking={tracking}
                />
                {shippingRestricted && (
                  <Notification
                    className={bem('shipping-restricted')}
                    message={messages?.notifications?.shippingRestricted?.() || ''}
                    type="error"
                  />
                )}
              </>
            );
          
        }
      
        // default case: addresses exist
        return (
          <Fragment>
            {addresses.length > 0 && (
              <RadioButtonGroup divided className={bem('radioGroup', { split })} removeLastDivider={isEditing}>
                {addresses.map((address: FormValues) => {
                  const isRestricted =
                    initialValues && initialValues.addressId === address.addressId && shippingRestricted;
      
                  const actualEditAddressId = saveEditing > 1 ? saveEditing : editAddressID;
      
                  if (isEditing && actualEditAddressId !== address.addressId) {
                    return null;
                  }
      
                  return (
                    <AddressRadioButton
                      key={Number(address.addressId)}
                      address={address}
                      checkoutAnalytics={checkoutAnalytics}
                      config={config}
                      defaultAddress={defaultAddress ? String(defaultAddress.addressId) : undefined}
                      editAddress={editAddress}
                      editAddressID={actualEditAddressId}
                      editable={editable}
                      hideCountry={hideCountry}
                      invalidFields={billingAddressValidation?.invalidFields || []}
                      isCheckoutLayout={isCheckoutLayout}
                      isGuestUser={isGuestUser}
                      isRestricted={isRestricted}
                      locale={locale}
                      match={match}
                      messages={messages}
                      onSubmit={handleSubmit}
                      primaryCategory={primaryCategory}
                      reduxForm={reduxForm}
                      removeAddressFinder={removeAddressFinder}
                      saveEditing={saveEditing}
                      setEditAddressID={setEditAddressID}
                      setRemoveAddressFinder={setRemoveAddressFinder}
                      setSaveEditing={setSaveEditing}
                      split={split}
                      trackEditAddress={() => trackEditAddress?.()}
                      trackUpdateAddress={trackUpdateAddress}
                      tracking={tracking}
                      onCancel={() => {
                        const klarnaPhoneNoError = reduxForm?.syncErrors?.billingAddress?.isKlarna;
                        setEditAddressID('');
                        setSelectAddressForm?.(klarnaPhoneNoError);
                        setAddressForm?.(false);
                        handleFormCancel?.();
                      }}
                      onChange={() => {
                        setEnableContinue(true);
                        onChange?.(address.addressId);
                        setSelectAddressForm?.(true);
                        setAddressForm?.(false);
                      }}
                    />
                  );
                })}
      
                {showChangeAddress && (
                  <FormField key="changeAddress" name="addressId" value="">
                    {({ value, ...rest }: FieldRenderProps) => {
                      const checked = value === 'changeAddress';
                      return (
                        <RadioButton
                          {...rest}
                          key="changeAddress"
                          checked={checked}
                          className={bem('changeAddress', split ? 'split' : undefined, 'radio')}
                          id="changeAddress"
                          label={messages.shippingAddressForm?.changeAddress?.()}
                          value="changeAddress"
                          onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                            onChange?.(ev.target.value);
                          }}
                        />
                      );
                    }}
                  </FormField>
                )}
      
                {addAddress && !isEditing && !showChangeAddress && (
                  <AddAddressRadioButton
                    address="addAddress"
                    messages={messages}
                    split={split}
                    trackAddNewAddress={trackAddNewAddress}
                    onChange={() => {
                      setEditAddressID('add');
                      onChange?.('add');
                    }}
                  />
                )}
              </RadioButtonGroup>
            )}
      
            {addAddress && isAdding && (
              <StopPropagation>
                <AddAddressRoute
                  addAddress={addAddress}
                  addTitle={addTitle}
                  checkoutAnalytics={checkoutAnalytics}
                  config={config}
                  formTitle={messages.addressSelectionForm?.addAddress?.()}
                  hideCountry={hideCountry}
                  history={history}
                  isCheckoutLayout={isCheckoutLayout}
                  locale={locale}
                  match={match}
                  messages={messages}
                  onSubmit={handleSubmit}
                  primaryCategory={primaryCategory}
                  removeAddressFinder={removeAddressFinder}
                  setEditAddressID={setEditAddressID}
                  setRemoveAddressFinder={setRemoveAddressFinder}
                  trackConfirmAddNewAddress={trackConfirmAddNewAddress}
                  tracking={tracking}
                  withOverlay={false}
                />
              </StopPropagation>
            )}
      
            {!addAddress && (
              <>
                <Route
                  exact
                  path={`${match.path}/edit/:addressId`}
                  render={({ match: editMatch }) => (
                    <EditAddressRoute
                      withOverlay
                      addresses={addresses}
                      checkoutAnalytics={checkoutAnalytics}
                      config={config}
                      editAddress={editAddress}
                      editMatch={editMatch}
                      editTitle={editTitle}
                      hideCountry={hideCountry}
                      history={history}
                      isCheckoutLayout={isCheckoutLayout}
                      locale={locale}
                      match={match}
                      messages={messages}
                      primaryCategory={primaryCategory}
                      removeAddressFinder={removeAddressFinder}
                      setRemoveAddressFinder={setRemoveAddressFinder}
                      trackUpdateAddress={trackUpdateAddress}
                      tracking={tracking}
                    />
                  )}
                />
                <Route
                  exact
                  path={`${match.path}/add`}
                  render={() => (
                    <AddAddressRoute
                      addAddress={addAddress}
                      addTitle={addTitle}
                      checkoutAnalytics={checkoutAnalytics}
                      config={config}
                      hideCountry={hideCountry}
                      history={history}
                      isCheckoutLayout={isCheckoutLayout}
                      locale={locale}
                      match={match}
                      messages={messages}
                      primaryCategory={primaryCategory}
                      removeAddressFinder={removeAddressFinder}
                      setRemoveAddressFinder={setRemoveAddressFinder}
                      trackConfirmAddNewAddress={trackConfirmAddNewAddress}
                      tracking={tracking}
                    />
                  )}
                />
              </>
            )}
          </Fragment>
        );
      }

    return (
        <Form
            className={formClass}
            collateErrors={collateErrors}
            initialValues={initialValues}
            messages={messages}
            name={name || 'address-form'}
            onSubmit={onSubmit ? handleSubmit : undefined}
            reduxForm={reduxForm}
            ref={childRef}
            touched={addressFormTouched}
        >
            {(submitting: boolean, errors: Array<string>) => (
                <Fragment>
                    {renderAddress()}

                    {!reduxForm && !(isAdding || isEditing) ? (
                        <Tooltip
                            key={error}
                            childWrapperClassName="checkout"
                            className={bem('buttonWrapper', 'isCheckoutLayout')}
                            defaultOpen={!submitting && !!errors.length}
                            message={errors?.[0]}
                            open={!submitting && !!errors.length}
                            type="error"
                        >
                            <SimpleAsyncButton
                                className={bem('submit')}
                                copy={messages.addressSelectionForm?.submit?.()}
                                disabled={!!isRegisteredAndRestricted && !enableContinue}
                                loading={submitting}
                                type="primary"
                                onClick={() => {
                                    removeAddressFinder?.();
                                    trackOnSubmitButtonClick?.();
                                }}
                            />
                        </Tooltip>
                    ) : null}
                </Fragment>
            )}
        </Form>
    );
};

export default withRouter(track()(AddressSelectionForm));
